import { useState, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/**
 * Fetches autocomplete suggestions for `query` against /api/search/suggestions,
 * debounced and with its own AbortController - deliberately separate from
 * Hero.jsx's main-search AbortController, so a suggestion keystroke never
 * cancels an in-flight real search and vice versa. The server is the single
 * source of truth for the searchSuggestions.ui feature flag: it returns an
 * empty list when the flag is off, so this hook needs no client-side flag
 * check (and avoids depending on window.featureFlags, which isn't populated
 * yet on a fresh page load before any other flag-merging request resolves).
 *
 * @param {string} query - raw (non-debounced) search box value
 * @returns {string[]} suggestions - up to a handful of suggested search terms
 */
export function useSearchSuggestions(query) {
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const [suggestions, setSuggestions] = useState([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const trimmed = debouncedQuery?.trim() || '';
    abortControllerRef.current?.abort();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    fetch(`/api/search/suggestions?q=${encodeURIComponent(trimmed)}`, {
      signal: abortController.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (abortControllerRef.current === abortController) {
          setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Search suggestions fetch failed:', error);
        if (abortControllerRef.current === abortController) {
          setSuggestions([]);
        }
      });

    return () => abortController.abort();
  }, [debouncedQuery]);

  return suggestions;
}
